import React, { useState, useRef, useEffect } from 'react'
import {
  Search,
  Plus,
  ChevronDown,
  ArrowUpDown,
  FolderArchive,
  RefreshCw,
  Settings,
  Database,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { ClassItem, IndexStatus } from '@/types/catalog'
import { Category, ClassThumbnail } from '@/types'
import MobileCatalogClassItem from './MobileCatalogClassItem'

interface MobileCatalogClassListProps {
  classes: ClassItem[]
  indexStatus: IndexStatus
  isAdmin: boolean
  classFilter: string
  sortBy: 'lastModified' | 'imageCount' | 'name'
  sortOrder: 'asc' | 'desc'
  getClassCategory: (classId: string) => Category | null
  getClassThumbnail: (classId: string) => ClassThumbnail | null
  onSelectClass: (classId: string) => void
  onEditClass: (classId: string) => void
  onDeleteClass: (classId: string) => Promise<void>
  onFilterChange: (filter: string) => void
  onSortChange: (sortBy: 'lastModified' | 'imageCount' | 'name') => void
  onToggleSortOrder: () => void
  onCreateClass: () => void
  onImportZip: () => void
  onRefresh: () => Promise<void>
  onManageCategories: () => void
}

const MobileCatalogClassList: React.FC<MobileCatalogClassListProps> = ({
  classes,
  indexStatus,
  isAdmin,
  classFilter,
  sortBy,
  sortOrder,
  getClassCategory,
  getClassThumbnail,
  onSelectClass,
  onEditClass,
  onDeleteClass,
  onFilterChange,
  onSortChange,
  onToggleSortOrder,
  onCreateClass,
  onImportZip,
  onRefresh,
  onManageCategories,
}) => {
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [showNewMenu, setShowNewMenu] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const sortDropdownRef = useRef<HTMLDivElement>(null)
  const newMenuRef = useRef<HTMLDivElement>(null)

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
        setShowSortDropdown(false)
      }
      if (newMenuRef.current && !newMenuRef.current.contains(e.target as Node)) {
        setShowNewMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setIsRefreshing(false)
    }
  }

  const getSortLabel = () => {
    switch (sortBy) {
      case 'lastModified':
        return 'Modified'
      case 'imageCount':
        return 'Images'
      case 'name':
        return 'Name'
      default:
        return 'Modified'
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 safe-area-top">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Catalog</h1>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span>{indexStatus.totalClasses} classes</span>
              <span className="text-gray-300">|</span>
              <span>{indexStatus.totalImages} images</span>
              <span
                className={cn(
                  'text-xs font-medium px-1.5 py-0.5 rounded-full',
                  indexStatus.isBuilding
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                )}
              >
                {indexStatus.isBuilding ? 'Building' : 'Ready'}
              </span>
            </div>
          </div>

          {/* Header actions */}
          {isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 text-gray-600 active:bg-gray-100 rounded-lg"
              >
                <RefreshCw className={cn('h-5 w-5', isRefreshing && 'animate-spin')} />
              </button>

              {/* New button with dropdown */}
              <div ref={newMenuRef} className="relative">
                <button
                  onClick={() => setShowNewMenu(!showNewMenu)}
                  className="flex items-center gap-1 px-3 py-2 bg-primary-600 text-white rounded-lg active:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span className="font-medium">New</span>
                </button>

                {showNewMenu && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowNewMenu(false)
                          onCreateClass()
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 active:bg-gray-50"
                      >
                        <Database className="h-4 w-4 text-gray-500" />
                        <span>Create empty class</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowNewMenu(false)
                          onImportZip()
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 active:bg-gray-50"
                      >
                        <FolderArchive className="h-4 w-4 text-gray-500" />
                        <span>Import from archive</span>
                      </button>
                      <div className="border-t my-1" />
                      <button
                        onClick={() => {
                          setShowNewMenu(false)
                          onManageCategories()
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 active:bg-gray-50"
                      >
                        <Settings className="h-4 w-4 text-gray-500" />
                        <span>Manage categories</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={classFilter}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder="Search classes..."
            className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-colors"
          />
        </div>

        {/* Sort dropdown */}
        <div className="flex items-center gap-2">
          <div ref={sortDropdownRef} className="relative">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg active:bg-gray-200 transition-colors"
            >
              <ArrowUpDown className="h-3.5 w-3.5" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showSortDropdown && 'rotate-180')} />
            </button>

            {showSortDropdown && (
              <div className="absolute left-0 mt-1 w-40 bg-white rounded-xl shadow-lg border z-50">
                <div className="py-1">
                  {(['lastModified', 'imageCount', 'name'] as const).map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        onSortChange(option)
                        setShowSortDropdown(false)
                      }}
                      className={cn(
                        'w-full px-4 py-2.5 text-left text-sm active:bg-gray-50',
                        sortBy === option ? 'text-primary-600 font-medium' : 'text-gray-700'
                      )}
                    >
                      {option === 'lastModified' && 'Modified Date'}
                      {option === 'imageCount' && 'Image Count'}
                      {option === 'name' && 'Name'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={onToggleSortOrder}
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 active:bg-gray-200 transition-colors',
              sortOrder === 'asc' ? 'text-gray-600' : 'text-gray-600'
            )}
          >
            <span className="text-xs font-medium">{sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
          </button>
        </div>
      </div>

      {/* Classes List */}
      <div className="flex-1 overflow-y-auto pb-20">
        {classes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Database className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-lg font-medium text-gray-700">No classes found</p>
            <p className="text-sm">
              {classFilter ? 'Try a different search term' : 'Create your first class to get started'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {classes.map((classItem) => (
              <MobileCatalogClassItem
                key={classItem.id}
                classItem={classItem}
                category={getClassCategory(classItem.id)}
                thumbnail={getClassThumbnail(classItem.id)}
                isAdmin={isAdmin}
                onTap={() => onSelectClass(classItem.id)}
                onEdit={() => onEditClass(classItem.id)}
                onDelete={() => onDeleteClass(classItem.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MobileCatalogClassList
